
import { Product } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { Package, AlertTriangle, Edit, Trash2, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { getStatusColor } from "./utils/product-status";
import { calculateDaysOfSupply, needsRestocking } from "@/lib/storage/stock/helpers";
import { useNavigate } from "react-router-dom";

interface ProductCardHeaderProps {
  product: Product;
  onEditClick: () => void;
  onDeleteClick: () => void;
}

const ProductCardHeader = ({ product, onEditClick, onDeleteClick }: ProductCardHeaderProps) => {
  const daysRemaining = calculateDaysOfSupply(product);
  const needsRestock = needsRestocking(product);
  const statusColor = getStatusColor(daysRemaining, product.minStockAlert);
  const navigate = useNavigate();

  const handleEditClick = () => {
    console.log(`Navigating to edit page for product: ${product.id}`);
    navigate(`/edit-product/${product.id}`);
    // Also call the parent component's onEditClick handler
    onEditClick();
  };

  return (
    <CardHeader className="pb-2">
      <div className="flex justify-between items-start">
        <div className="flex items-center">
          <Badge className="mr-2" variant="outline">
            {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
          </Badge>
          <Badge className={statusColor}>
            {daysRemaining === Infinity ? "∞" : `${daysRemaining} days left`}
          </Badge>
        </div>
        <div className="flex items-center">
          {needsRestock && (
            <AlertTriangle className="h-5 w-5 text-yellow-500 animate-pulse mr-2" />
          )}
          <div className="flex space-x-1">
            <button
              onClick={handleEditClick}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Edit Product"
            >
              <Edit className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
            <button
              onClick={onDeleteClick}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Delete Product"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      </div>
      <CardTitle className="flex items-center mt-2 text-lg">
        <Package className="h-5 w-5 mr-2 text-teal-500" />
        {product.name}
      </CardTitle>
    </CardHeader>
  );
};

export default ProductCardHeader;
